import { z } from 'zod'
import { LocaleSchema } from './auth'
import { DateStringSchema } from './booking'

// Joining the club: the public application form at /join, the fee
// schedules it shows, and the membership secretary's review.
// Tables: member.membership_fees, member.membership_applications.

// The types an applicant can choose. Only full and junior members may be
// granted the right to book and fly the club's aircraft.
export const MEMBERSHIP_TYPES = ['full', 'junior', 'supporting'] as const
export type MembershipType = (typeof MEMBERSHIP_TYPES)[number]

// What a member record can be: an applicant's choice, or an observer -- not
// a club member but someone who takes part in the palolento flights, created
// by an administrator and never offered on the public form.
export const MEMBER_TYPES = [...MEMBERSHIP_TYPES, 'observer'] as const
export type MemberType = (typeof MEMBER_TYPES)[number]

// The old WordPress form's "Lupakirjatiedot" and "Lupakirjatyyppi".
export const LICENCE_LEVELS = ['none', 'student', 'lapl_ppl', 'cpl_mpl', 'atpl', 'mechanic'] as const
export type LicenceLevel = (typeof LICENCE_LEVELS)[number]
export const LICENCE_RATINGS = ['vfr', 'night', 'ir', 'instructor', 'examiner'] as const
export type LicenceRating = (typeof LICENCE_RATINGS)[number]

export const APPLICATION_STATUSES = ['pending', 'approved', 'rejected'] as const
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number]

// ---- fee schedules ---------------------------------------------------------

const Fee = z.number().nonnegative().max(99999.99)

export const FeeScheduleSchema = z
  .object({
    validFrom: DateStringSchema,
    // Inclusive. null = in force until further notice (the current schedule).
    validTo: DateStringSchema.nullable(),
    fullAnnualFee: Fee,
    fullJoiningFee: Fee,
    juniorAnnualFee: Fee,
    juniorJoiningFee: Fee,
    supportingAnnualFee: Fee,
    supportingJoiningFee: Fee,
  })
  .refine((v) => v.validTo === null || v.validTo >= v.validFrom, {
    message: 'The end date cannot be before the start date',
    path: ['validTo'],
  })
export type FeeScheduleInput = z.infer<typeof FeeScheduleSchema>

// A schedule as the API returns it.
export interface FeeSchedule extends FeeScheduleInput {
  updatedAt?: string
}

type Period = Pick<FeeScheduleInput, 'validFrom' | 'validTo'>

// YYYY-MM-DD arithmetic in UTC, where a day is always 24 hours.
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

const overlaps = (a: Period, b: Period) =>
  a.validFrom <= (b.validTo ?? '9999-12-31') && b.validFrom <= (a.validTo ?? '9999-12-31')

// The schedule in force on `date`, if any.
export function feeScheduleOn<T extends Period>(schedules: readonly T[], date: string): T | undefined {
  return schedules.find((s) => s.validFrom <= date && (s.validTo === null || date <= s.validTo))
}

export type FeeSchedulePlan =
  // Saving is fine. `closeCurrent`: the open-ended schedule starting on that
  // date gets `validTo` set first, so the new one can take over from it.
  | { ok: true; closeCurrent?: { validFrom: string; validTo: string } }
  | { ok: false; conflictsWith: string }

// Whether `candidate` can be saved alongside `existing` -- periods must not
// overlap and only one may be open-ended (member.membership_fees enforces
// both; this says so before the database has to).
//
// The one overlap resolved rather than refused is the usual yearly change:
// a new schedule starting after the current open-ended one ends it the day
// before. `replacing` is the validFrom of the row being edited, left out of
// the comparison.
export function planFeeSchedule(existing: readonly Period[], candidate: Period, replacing?: string): FeeSchedulePlan {
  const others = existing.filter((s) => s.validFrom !== replacing)
  const current = others.find((s) => s.validTo === null)

  let closeCurrent: { validFrom: string; validTo: string } | undefined
  if (current && current.validFrom < candidate.validFrom && candidate.validTo === null) {
    closeCurrent = { validFrom: current.validFrom, validTo: addDays(candidate.validFrom, -1) }
  }

  for (const other of others) {
    const period = closeCurrent && other.validFrom === closeCurrent.validFrom ? closeCurrent : other
    if (overlaps(period, candidate)) return { ok: false, conflictsWith: other.validFrom }
  }
  return closeCurrent ? { ok: true, closeCurrent } : { ok: true }
}

// ---- applications ----------------------------------------------------------

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => v || null)

export const MembershipApplicationSchema = z.object({
  membershipType: z.enum(MEMBERSHIP_TYPES),
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  dateOfBirth: DateStringSchema.refine((v) => v >= '1900-01-01' && v <= new Date().toISOString().slice(0, 10), {
    message: 'Not a valid date of birth',
  }),
  email: z.string().trim().toLowerCase().email().max(254),
  phone: z.string().trim().min(5).max(40).regex(/^[+\d][\d\s()-]*$/, 'Not a valid phone number'),
  address: z.string().trim().min(1).max(200),
  postalCode: z.string().trim().min(1).max(20),
  city: z.string().trim().min(1).max(100),
  licenceLevel: z.enum(LICENCE_LEVELS).nullish().transform((v) => v ?? null),
  licenceRatings: z.array(z.enum(LICENCE_RATINGS)).max(LICENCE_RATINGS.length).default([]),
  licenceExpiredYear: z.number().int().min(1900).max(2100).nullish().transform((v) => v ?? null),
  trainingDetails: optionalText(200),
  flightHours: z.number().nonnegative().max(99999).nullish().transform((v) => v ?? null),
  additionalInfo: optionalText(4000),
  locale: LocaleSchema,
  acceptRules: z.literal(true),
  acceptPrivacy: z.literal(true),
  turnstileToken: z.string().max(2048).optional(),
  // Honeypot: hidden from people, filled in by naive bots. Anything in it
  // and the application is quietly dropped (routes/publicMembership.ts).
  website: z.string().max(500).optional(),
})
export type MembershipApplicationInput = z.input<typeof MembershipApplicationSchema>

export const VerifyApplicationEmailSchema = z.object({ token: z.string().min(1).max(200) })

export const ReviewApplicationSchema = z.object({
  status: z.enum(APPLICATION_STATUSES),
  reviewNote: optionalText(2000),
})
export type ReviewApplicationInput = z.input<typeof ReviewApplicationSchema>

// An application as the admin app sees it.
export interface MembershipApplication {
  applicationId: number
  membershipType: MembershipType
  firstName: string
  lastName: string
  dateOfBirth: string
  email: string
  phone: string
  address: string
  postalCode: string
  city: string
  licenceLevel: LicenceLevel | null
  licenceRatings: LicenceRating[]
  licenceExpiredYear: number | null
  trainingDetails: string | null
  flightHours: number | null
  additionalInfo: string | null
  locale: 'fi' | 'sv' | 'en'
  rulesAcceptedAt: string
  privacyAcceptedAt: string
  // When the applicant followed the link in their confirmation email.
  emailVerifiedAt: string | null
  feeValidFrom: string | null
  status: ApplicationStatus
  reviewNote: string | null
  reviewedBy: string | null
  reviewedByName: string | null
  reviewedAt: string | null
  submittedAt: string
}

// GET /api/public/membership: everything the application form shows
// before the applicant has typed anything.
export interface MembershipInfo {
  fees: FeeSchedule | null
  rulesUrl: string | null
  contactUrl: string | null
  privacyUrl: string | null
}
